/**
 * 费用报表（延伸需求 3.7）
 *
 * 六张报表共用一条聚合查询，只有分组维度不同：按人 / 按部门 / 按类型 / 按月 / 按工程 / 按预算。
 *
 * 【为什么是一个页面加维度切换，而不是六个页面】六张报表的筛选条件完全一样，
 * 六个页面意味着改一次筛选逻辑要改六处——而"按人报表含某单、按部门报表不含"
 * 这种对不上的账，恰恰是这么来的。
 *
 * 【口径】统计的是**已通过及之后**的报销金额（APPROVED / PAID / ARCHIVED），
 * 与预算执行的"已用"完全同一个口径。草稿与在途不进来，否则"本月费用"
 * 会随着别人提交草稿而跳动。
 */

import { useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Progress, Row, Select, Space, Statistic, Table, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ExpenseType, EXPENSE_TYPE_LABELS, PermissionCode } from '@ilc/shared';
import { expenseApi, orgApi } from '@/api/endpoints';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { Money, moneyCellStyle } from '@/components/Money';
import { exportCsv, exportMoney } from '@/utils/export';
import { feedback } from '@/utils/feedback';

type Dimension = 'by-person' | 'by-dept' | 'by-type' | 'by-month' | 'by-project' | 'budget';

const DIMENSIONS: Array<{ value: Dimension; label: string }> = [
  { value: 'by-person', label: '按人' },
  { value: 'by-dept', label: '按部门' },
  { value: 'by-type', label: '按费用类型' },
  { value: 'by-month', label: '按月份' },
  { value: 'by-project', label: '按工程' },
  { value: 'budget', label: '预算执行' },
];

export function ExpenseReportsPage(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canView = can(PermissionCode.EXPENSE_REPORT);

  const [dimension, setDimension] = useState<Dimension>('by-type');
  const [period, setPeriod] = useState<string | undefined>(dayjs().format('YYYY'));
  const [expenseType, setExpenseType] = useState<ExpenseType | undefined>(undefined);
  const [deptId, setDeptId] = useState<string | undefined>(undefined);

  const depts = useResource(() => orgApi.depts(), { deps: [] });

  /** 预算维度走另一个端点（行是预算，不是分组聚合） */
  const isBudget = dimension === 'budget';

  const report = useResource(
    () =>
      isBudget
        ? Promise.resolve(null)
        : expenseApi.report(dimension as Exclude<Dimension, 'budget'>, {
            period,
            expenseType,
            deptId,
          }),
    { deps: [dimension, period, expenseType, deptId], enabled: !isBudget },
  );

  const execution = useResource(
    () => (isBudget ? expenseApi.budgetExecution({ period, expenseType, deptId }) : Promise.resolve([])),
    { deps: [dimension, period, expenseType, deptId], enabled: isBudget },
  );

  if (!canView) {
    return (
      <div className="ilc-page">
        <PageHeader title="费用报表" />
        <Alert type="warning" showIcon message="你没有查看费用报表的权限" />
      </div>
    );
  }

  const data = report.data;
  const rows = data?.rows ?? [];

  return (
    <div className="ilc-page">
      <PageHeader
        title="费用报表"
        description="统计口径：已通过及之后的报销金额（与预算执行的「已用」同一口径）"
        extra={
          <Space>
            <Button
              disabled={isBudget || rows.length === 0}
              onClick={() => {
                if (!data) return;
                exportCsv(
                  `费用报表-${DIMENSIONS.find((d) => d.value === dimension)?.label ?? ''}`,
                  [
                    { title: '分组', dataIndex: 'groupLabel' },
                    { title: '笔数', dataIndex: 'count' },
                    { title: '金额(元)', value: (r: { amountCents: number }) => exportMoney(r.amountCents) },
                    { title: '占比', value: (r: { ratio: number }) => `${(r.ratio * 100).toFixed(2)}%` },
                  ],
                  rows,
                );
                // 导出会把金额带出系统，留一条提示让操作者知道这件事有记录
                feedback.info('已导出为 CSV。导出动作在后端留有审计记录。');
              }}
            >
              导出 CSV
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                report.reload();
                execution.reload();
              }}
            >
              刷新
            </Button>
          </Space>
        }
      />

      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <span>维度：</span>
          {DIMENSIONS.map((d) => (
            <Button
              key={d.value}
              type={dimension === d.value ? 'primary' : 'default'}
              onClick={() => setDimension(d.value)}
            >
              {d.label}
            </Button>
          ))}
          <DatePicker
            picker="year"
            allowClear
            placeholder="年度"
            value={period ? dayjs(period, 'YYYY') : null}
            onChange={(d) => setPeriod(d ? d.format('YYYY') : undefined)}
          />
          <Select
            allowClear
            placeholder="费用类型"
            style={{ width: 130 }}
            value={expenseType}
            onChange={setExpenseType}
            options={Object.entries(EXPENSE_TYPE_LABELS).map(([value, label]) => ({ value, label }))}
          />
          <Select
            allowClear
            placeholder="部门"
            style={{ width: 140 }}
            value={deptId}
            onChange={setDeptId}
            loading={depts.loading}
            options={(depts.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
          />
        </Space>
      </Card>

      {isBudget ? (
        <PageState loading={execution.loading} error={execution.error} onRetry={execution.reload}>
          <Table
            size="small"
            rowKey="budgetId"
            dataSource={execution.data ?? []}
            pagination={false}
            locale={{ emptyText: '当前筛选下没有预算' }}
            columns={[
              { title: '期间', dataIndex: 'period', width: 90 },
              { title: '部门', dataIndex: 'deptName', width: 110, render: (v: string | null) => v ?? '全公司' },
              { title: '工程', dataIndex: 'projectName', width: 120, render: (v: string | null) => v ?? '不限' },
              {
                title: '费用类型',
                dataIndex: 'expenseType',
                width: 100,
                render: (v: string) => EXPENSE_TYPE_LABELS[v as ExpenseType] ?? v,
              },
              {
                title: '预算金额',
                dataIndex: 'budgetCents',
                align: 'right' as const,
                onCell: () => ({ style: moneyCellStyle }),
                render: (v: number) => <Money cents={v} />,
              },
              {
                title: '已批准调整',
                dataIndex: 'adjustCents',
                align: 'right' as const,
                onCell: () => ({ style: moneyCellStyle }),
                render: (v: number) => (v === 0 ? '-' : <Money cents={v} colorNegative={false} />),
              },
              {
                title: '当前预算',
                dataIndex: 'currentCents',
                align: 'right' as const,
                onCell: () => ({ style: moneyCellStyle }),
                render: (v: number) => <Money cents={v} strong />,
              },
              {
                title: '已用',
                dataIndex: 'usedCents',
                align: 'right' as const,
                onCell: () => ({ style: moneyCellStyle }),
                render: (v: number) => <Money cents={v} />,
              },
              {
                title: '在途',
                dataIndex: 'pendingCents',
                align: 'right' as const,
                onCell: () => ({ style: moneyCellStyle }),
                render: (v: number) => <Money cents={v} />,
              },
              {
                title: '执行率',
                dataIndex: 'usedRatio',
                width: 170,
                render: (v: number | null) =>
                  v === null ? (
                    <span style={{ color: 'rgba(0,0,0,0.35)' }}>-</span>
                  ) : (
                    <Space size={4}>
                      <Progress
                        percent={Math.min(100, Math.round(v * 100))}
                        size="small"
                        status={v > 1 ? 'exception' : v >= 0.8 ? 'active' : 'normal'}
                        style={{ width: 80 }}
                      />
                      {v > 1 && <Tag color="error">超支</Tag>}
                    </Space>
                  ),
              },
            ]}
          />
        </PageState>
      ) : (
        <PageState
          loading={report.loading}
          error={report.error}
          empty={!report.loading && !report.error && rows.length === 0}
          emptyText="当前筛选下没有已通过的费用数据"
          onRetry={report.reload}
        >
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={8}>
              <Card size="small">
                <Statistic title="合计金额" value={(data?.totalCents ?? 0) / 100} precision={2} prefix="¥" />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card size="small">
                <Statistic title="单据数" value={data?.totalCount ?? 0} suffix="张" />
              </Card>
            </Col>
            <Col xs={12} md={8}>
              <Card size="small">
                <Statistic title="分组数" value={rows.length} />
              </Card>
            </Col>
          </Row>

          {data?.truncated && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="分组数超过 500，报表已截断"
              description={
                <span>
                  合计数字仍是<strong>全量</strong>的（单独查的），但下面的明细行不全。
                  请缩小筛选范围（例如按季度或按部门）再导出——静默少几行的财务报表比报错危险得多。
                </span>
              }
            />
          )}

          <Table
            size="small"
            rowKey="groupKey"
            dataSource={rows}
            pagination={rows.length > 30 ? { pageSize: 30, showSizeChanger: false } : false}
            summary={() =>
              rows.length > 0 ? (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <strong>合计（全量）</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <strong>{data?.totalCount ?? 0}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <Money cents={data?.totalCents ?? 0} strong />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    100%
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              ) : null
            }
            columns={[
              { title: '分组', dataIndex: 'groupLabel', render: (v: string) => v || '（未填）' },
              { title: '笔数', dataIndex: 'count', width: 90, align: 'right' as const },
              {
                title: '金额',
                dataIndex: 'amountCents',
                width: 160,
                align: 'right' as const,
                onCell: () => ({ style: moneyCellStyle }),
                render: (v: number) => <Money cents={v} strong />,
              },
              {
                title: '占比',
                dataIndex: 'ratio',
                width: 200,
                render: (v: number) => (
                  <Space size={6}>
                    <Progress
                      percent={Math.round(v * 100)}
                      size="small"
                      style={{ width: 90 }}
                      showInfo={false}
                    />
                    <span>{(v * 100).toFixed(2)}%</span>
                  </Space>
                ),
              },
            ]}
          />
        </PageState>
      )}

      <Card size="small" title="报表口径" style={{ marginTop: 16 }}>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
          <li>
            <strong>统计范围</strong>：状态为「已通过 / 已付款 / 已归档」的报销单。
            草稿与审批中的<em>不计入</em>——否则"本月费用"会随着别人提交草稿而跳动。
          </li>
          <li>
            <strong>归期口径</strong>：按付款时间 → 通过时间 → 提交时间的顺序取第一个有值的。
            一张 8 月 31 日提交、9 月 2 日批准的单子算在 9 月（钱是那时认下的）。
          </li>
          <li>
            <strong>占比</strong>由服务端算：合计是权威值，前端各算一份会与合计对不上。
          </li>
          <li>
            <strong>数据权限</strong>：报表也按你的数据范围收口——「仅本人」的用户只看得到自己的费用。
          </li>
        </ul>
      </Card>
    </div>
  );
}

export default ExpenseReportsPage;
